<script setup>
import { onMounted, getCurrentInstance } from 'vue'
import { router, Head } from '@inertiajs/vue3'
import MainLayout from '@/layout/main/MainLayout.vue'
import { useLoading } from '@/composables/useLoading'
import UpdateProfileInformationForm from './Partials/UpdateProfileInformationForm.vue'
import UpdateCompanyForm from './Partials/UpdateCompanyForm.vue'
import UpdateEmailForm from './Partials/UpdateEmailForm.vue'
import UpdateContactForm from './Partials/UpdateContactForm.vue'
import UpdateInvoiceForm from './Partials/UpdateInvoiceForm.vue'
import UpdatePasswordForm from './Partials/UpdatePasswordForm.vue'

defineOptions({
    layout: MainLayout,
})

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const props = defineProps({
    orders: Number,
    usedCount: Number,
    unusedCount: Number,
    sessions: Array,
    currencies: Array,
    languages: Array,
})

const auth = proxy.$page.props.auth
const user = proxy.$page.props.user
const jetstream = proxy.$page.props.jetstream

onMounted(() => {
    const hash = window.location.hash.split('#')[1]

    if (hash) {
        setTimeout(() => {
            const el = document.getElementById(hash)

            if (el) {
                el.scrollIntoView({
                    behavior: "smooth"
                })
            }
        }, 250)
    }
})

// Navigation functions
const goToOrders = () => {
    showLoading($t('Loading orders...'));
    try {
        const routeName = proxy.route ? proxy.route('client.orders.index') : '/orders';
        router.visit(routeName, {
            onFinish: () => hideLoading(),
        });
    } catch (e) {
        hideLoading();
        window.location.href = '/orders';
    }
}

const goToProjects = () => {
    showLoading($t('Loading projects...'));
    try {
        const routeName = proxy.route ? proxy.route('client.projects.index') : '/projects';
        router.visit(routeName, {
            onFinish: () => hideLoading(),
        });
    } catch (e) {
        hideLoading();
        window.location.href = '/projects';
    }
}

const goToFavorites = () => {
    showLoading($t('Loading favorites...'));
    try {
        const routeName = proxy.route ? proxy.route('client.sites.list', { _query: { 'filter[favorites]': 'true' } }) : '/sites?filter[favorites]=true';
        router.visit(routeName, {
            onFinish: () => hideLoading(),
        });
    } catch (e) {
        hideLoading();
        window.location.href = '/sites?filter[favorites]=true';
    }
}

const goToInterests = () => {
    showLoading($t('Loading interests...'));
    try {
        const routeName = proxy.route ? proxy.route('client.sites.list', { _query: { 'filter[interests]': 'true' } }) : '/sites?filter[interests]=true';
        router.visit(routeName, {
            onFinish: () => hideLoading(),
        });
    } catch (e) {
        hideLoading();
        window.location.href = '/sites?filter[interests]=true';
    }
}
</script>

<template>
    <Head :title="$t('Profile')" />

    <div class="grid px-4 lg:px-0 w-full max-w-7xl mx-auto">
        <!-- Summary Card -->
        <Card class="mb-6">
            <template #content>
                <div class="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    <!-- Consultant Info -->
                    <div class="lg:col-span-3 lg:border-r border-surface-200 lg:pr-6">
                        <div class="flex flex-col space-y-3">
                            <h3 class="text-lg font-semibold text-surface-900">
                                {{ $t('Your current Linking consultant') }}
                            </h3>

                            <div v-if="auth.user.consultant" class="flex items-center gap-3">
                                <Avatar
                                    :image="auth.user.consultant.profile_photo_url"
                                    :label="auth.user.consultant.name.charAt(0)"
                                    class="w-10 h-10"
                                    shape="circle"
                                />
                                <div class="flex flex-col">
                                    <span class="font-medium text-surface-900">{{ auth.user.consultant.name }}</span>
                                    <span class="text-sm text-surface-600">{{ auth.user.consultant.email }}</span>
                                    <span v-if="auth.user.consultant.phone" class="text-sm text-surface-600">
                                        {{ auth.user.consultant.phone }}
                                    </span>
                                </div>
                            </div>

                            <div v-else class="text-surface-500 italic">
                                {{ $t('You do not have a selected consultant at the moment') }}
                            </div>
                        </div>
                    </div>

                    <!-- Statistics -->
                    <div class="lg:col-span-2">
                        <div class="flex flex-col space-y-2">
                            <h3 class="text-lg font-semibold text-primary-600">
                                {{ $t('So far, you have') }}
                            </h3>
                            <div class="space-y-1">
                                <div class="flex items-center gap-2">
                                    <i class="pi pi-shopping-cart text-surface-500"></i>
                                    <span class="text-surface-600">{{ orders }} {{ $t('orders') }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <i class="pi pi-check-circle text-green-500"></i>
                                    <span class="text-surface-600">{{ usedCount }} {{ $t('sites used') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Divider />

                <!-- Quick Actions -->
                <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Button
                        :label="$t('Your orders')"
                        icon="pi pi-shopping-cart"
                        @click="goToOrders"
                        class="p-button-outlined"
                    />
                    <Button
                        :label="$t('Your projects')"
                        icon="pi pi-folder"
                        @click="goToProjects"
                        class="p-button-outlined"
                    />
                    <Button
                        :label="$t('Your favorites')"
                        icon="pi pi-heart"
                        @click="goToFavorites"
                        class="p-button-outlined"
                    />
                    <Button
                        :label="$t('Your interests')"
                        icon="pi pi-star"
                        @click="goToInterests"
                        class="p-button-outlined"
                    />
                </div>
            </template>
        </Card>

        <!-- Account Settings Section -->
        <Card class="mb-6">
            <template #header>
                <div class="flex items-center gap-3 p-6 pb-0">
                    <i class="pi pi-user text-2xl text-primary-500"></i>
                    <div>
                        <h2 class="text-xl font-bold text-surface-900">{{ $t('Account settings') }}</h2>
                        <p class="text-surface-600">{{ $t('This is all about you :)') }}</p>
                    </div>
                </div>
            </template>

            <template #content>
                <div class="space-y-8">
                    <!-- Profile Information -->
                    <div class="bg-surface-50 p-6 rounded-lg">
                        <h3 class="text-lg font-semibold mb-4 text-surface-900">{{ $t('Profile Information') }}</h3>
                        <UpdateProfileInformationForm :user="user" :currencies="currencies" :languages="languages" />
                    </div>

                    <!-- Company Information -->
                    <div class="p-6 rounded-lg border border-surface-200">
                        <h3 class="text-lg font-semibold mb-4 text-surface-900">{{ $t('Company Information') }}</h3>
                        <UpdateCompanyForm :user="user" />
                    </div>

                    <!-- Contact Information -->
                    <div class="p-6 rounded-lg border border-surface-200">
                        <h3 class="text-lg font-semibold mb-4 text-surface-900">{{ $t('Contact Information') }}</h3>
                        <UpdateContactForm :user="user" />
                    </div>
                </div>
            </template>
        </Card>

        <!-- Invoice Information Section -->
        <Card class="mb-6">
            <template #header>
                <div class="flex items-center gap-3 p-6 pb-0">
                    <i class="pi pi-dollar text-2xl text-primary-500"></i>
                    <div>
                        <h2 class="text-xl font-bold text-surface-900">{{ $t('Invoice info') }}</h2>
                        <p class="text-surface-600">{{ $t('Make sure these fields are filled in correctly') }}</p>
                    </div>
                </div>
            </template>

            <template #content>
                <UpdateInvoiceForm :user="user" />
            </template>
        </Card>

        <!-- Password Section -->
        <Card v-if="jetstream?.canUpdatePassword" class="mb-6">
            <template #header>
                <div class="flex items-center gap-3 p-6 pb-0">
                    <i class="pi pi-key text-2xl text-primary-500"></i>
                    <div>
                        <h2 class="text-xl font-bold text-surface-900">{{ $t('Change password') }}</h2>
                        <p class="text-surface-600">{{ $t('Your password must be at least 8 characters long') }}</p>
                    </div>
                </div>
            </template>

            <template #content>
                <UpdatePasswordForm />
            </template>
        </Card>
    </div>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}
</style>
